import { useCallback, useState } from "react";

import { message, Tooltip } from "antd";
import { showErrorToast } from "V2.0/utils";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { Button } from "V2.0/common/button";
import {
  PauseCircleIcon,
  PhoneIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

function ConfirmPausePendingCalls(props) {
  const {
    onPausePendingCalls,
    fetchCurrentQueueStats,
    onCancel,
    pendingCalls,
  } = props;

  const [loading, setLoading] = useState(false);

  const handlePausePendingCalls = useCallback(
    function () {
      const lifecycleCbs = {
        init: () => {
          message.loading(`Pausing ${pendingCalls} calls`);
          setLoading(true);
        },
        success: (data: { message: string; pausedCount: number }) => {
          const successMessage =
            data?.message ?? `${pendingCalls} calls Paused`;
          message.destroy();
          message.success(successMessage);
          setLoading(false);
          fetchCurrentQueueStats();
          onCancel();
        },
        error: (e) => {
          message.destroy();
          setLoading(false);
          showErrorToast(e, "Failed to pause calls");
        },
      };

      onPausePendingCalls(lifecycleCbs);
    },
    [fetchCurrentQueueStats, onPausePendingCalls]
  );

  return (
    <Dialog
      open={true}
      onClose={onCancel}
    >
      <DialogTitle>{`Pause ${pendingCalls} calls ?`}</DialogTitle>
      <DialogBody></DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={loading}
          onClick={handlePausePendingCalls}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmCancelPendingCalls(props) {
  const {
    onCancelPendingCalls,
    fetchCurrentQueueStats,
    onCancel,
    pendingCalls,
  } = props;

  const [loading, setLoading] = useState(false);

  const handleCancelPendingCalls = useCallback(
    function () {
      const lifecycleCbs = {
        init: () => {
          message.loading(`Cancelling ${pendingCalls} Calls`);
          setLoading(true);
        },
        success: (data: { message: string; cancelledCount: number }) => {
          const successMessage =
            data?.message ?? `${pendingCalls} calls Cancelled`;
          message.destroy();
          message.success(successMessage);
          setLoading(false);
          fetchCurrentQueueStats();
          onCancel();
        },
        error: (e) => {
          message.destroy();
          showErrorToast(e, "Failed to Cancel Calls");
          setLoading(false);
        },
      };

      onCancelPendingCalls(lifecycleCbs);
    },
    [fetchCurrentQueueStats, onCancelPendingCalls]
  );

  return (
    <Dialog
      open={true}
      onClose={onCancel}
    >
      <DialogTitle>{`Cancel ${pendingCalls} calls ?`}</DialogTitle>
      <DialogBody></DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={loading}
          onClick={handleCancelPendingCalls}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PendingCallsLoading() {
  return (
    <div className="border rounded-lg w-full">
      <div className="h-8 rounded-t-lg px-4 py-2">
        <div className="animate-pulse bg-gray-100 h-4 w-24 rounded-sm"></div>
      </div>
      <div>
        <div className="flex items-baseline gap-x-2 p-4">
          <div className="animate-pulse bg-gray-100 h-10 w-10 p-4 rounded-sm" />
        </div>
      </div>
    </div>
  );
}

export default function PendingCalls(props) {
  const {
    isLoading,
    pendingCalls,
    onPausePendingCalls,
    onCancelPendingCalls,
    fetchCurrentQueueStats,
    locked,
  } = props;

  const [openConfirmPauseCalls, setOpenConfirmPauseCalls] =
    useState<boolean>(false);
  const [openConfirmCancelCalls, setOpenConfirmCancelCalls] =
    useState<boolean>(false);

  if (isLoading) {
    return <PendingCallsLoading />;
  }

  return (
    <>
      <div className="border rounded-lg w-full">
        <div className="flex justify-between items-center bg-gray-50 rounded-t-lg px-2">
          <p className="text-sm font-semibold">Pending</p>

          <div className="flex">
            <Tooltip title="Pause Calls">
              <Button
                plain
                disabled={pendingCalls === 0 || locked}
                onClick={() => setOpenConfirmPauseCalls(true)}
              >
                <PauseCircleIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Cancel Calls">
              <Button
                plain
                disabled={pendingCalls === 0 || locked}
                onClick={() => setOpenConfirmCancelCalls(true)}
              >
                <TrashIcon />
              </Button>
            </Tooltip>
          </div>
        </div>
        <div className=" p-4 flex items-baseline gap-x-2">
          <p className="text-xl font-semibold text-right">{pendingCalls}</p>
          <p className="text-sm font-light">Calls</p>
        </div>
      </div>
      {openConfirmCancelCalls && (
        <ConfirmCancelPendingCalls
          pendingCalls={pendingCalls}
          onCancel={() => setOpenConfirmCancelCalls(false)}
          onCancelPendingCalls={onCancelPendingCalls}
          fetchCurrentQueueStats={fetchCurrentQueueStats}
        />
      )}
      {openConfirmPauseCalls && (
        <ConfirmPausePendingCalls
          pendingCalls={pendingCalls}
          onCancel={() => setOpenConfirmPauseCalls(false)}
          onPausePendingCalls={onPausePendingCalls}
          fetchCurrentQueueStats={fetchCurrentQueueStats}
        />
      )}
    </>
  );
}
